import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { components } from 'react-select';
import ACS from 'react-select/async-creatable';
import AS from 'react-select/async';
import Select from 'react-select';
import './style.scss';

const styles = ({ height, isInvalid }) => {
  const hasFixedHeight = height != null && height !== '';

  const boxHeight = hasFixedHeight
    ? {
        height,
        minHeight: height,
        maxHeight: height
      }
    : {};

  return {
    control: (provided, state) => ({
      ...provided,
      ...boxHeight,
      borderColor: isInvalid ? 'red !important' : provided.borderColor
    }),
    menuList: (provided, state) => ({
      ...provided,
      maxHeight: '200px',
      overflowY: 'auto',
      textWrap: 'wrap',
      zIndex: 9999999999
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      ...(hasFixedHeight ? boxHeight : { minHeight: height })
    }),
    input: (provided, state) => ({
      ...provided,
      ...(hasFixedHeight
        ? {
            margin: 0,
            paddingTop: 0,
            paddingBottom: 0,
            height: '100%',
            minHeight: 0
          }
        : {})
    }),
    indicatorsContainer: (provided, state) => ({
      ...provided,
      ...(hasFixedHeight
        ? {
            ...boxHeight,
            alignSelf: 'center',
            paddingTop: 0,
            paddingBottom: 0
          }
        : {})
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      ...(hasFixedHeight ? { padding: '0 4px' } : {})
    }),
    clearIndicator: (provided, state) => ({
      ...provided,
      ...(hasFixedHeight ? { padding: '0 2px' } : {})
    }),
    menuPortal: (provided, state) => ({
      ...provided,
      zIndex: 9999999999,
      minWidth: '80px'
    }),
    groupHeading: base => ({
      ...base,
      fontWeight: 600,
      fontSize: '12px'
    }),
    group: base => ({
      ...base,
      paddingTop: 0,
      background: 'rgb(237, 237, 237)'
    }),
    option: (provided, { data, isDisabled, isFocused, isSelected }) => {
      let { backgroundColor, textColor, hoverColor } = data || {};
      return {
        ...provided,
        backgroundColor: isSelected ? backgroundColor || 'rgb(35, 127, 255)' : undefined,
        color: isSelected ? 'white' : undefined,
        ':hover': {
          backgroundColor: hoverColor || 'rgb(35, 127, 255)',
          color: 'white',
          '& *': {
            backgroundColor: 'transparent !important',
            color: 'white !important'
          }
        }
      };
    }
  };
};

const AsyncCreatableSelect = ({
  items,
  selectedItems,
  onChange,
  includeSelectAll = false,
  maxToShow = 10,
  placeholder = '',
  height,
  showMessageOnlyOnOverflow = false,
  maxItemCustomMessage = length => `+ ${length} user${length === 1 ? '' : 's'} selected`,
  selectAllLabel = 'All Items',
  isMulti = false,
  isGroupped = false,
  closeMenuOnSelect = false,
  closeMenuOnScroll = true,
  fieldColors = {},
  isClearable = false,
  disabled,
  loadOptions,
  onCreateOption,
  isLoading = false,
  CustomSingleValueComponent,
  CustomMultiValueComponent,
  CustomOptionComponent,
  creatable = false,
  searchable = false,
  originalOptions = [],
  creatableLabel = 'Create',
  noOptionsMessage = 'No items, type to create...',
  isInvalid,
  useMenuPortal = true
}) => {
  const [inputValue, setInputValue] = useState('');
  const selectAllOption = useMemo(() => ({ label: selectAllLabel, value: 'all' }), [selectAllLabel]);

  // Store current options for filtering
  const [currOptions, setCurrOptions] = useState([]);
  const [stopFetching, setStopFetching] = useState(false);

  // Update current options when items change
  useEffect(() => {
    if (items && items.length > 0) {
      const mappedOptions = items.map(item => ({
        label: item?.label || item?.name,
        value: item.value || item._id,
        ...item
      }));

      setCurrOptions(stopFetching ? originalOptions : mappedOptions);
    }
  }, [items, stopFetching]);

  const handleBlur = () => {
    const selectedOption =
      !isMulti &&
      inputValue &&
      items?.find(
        i =>
          i?.value === inputValue ||
          i.id === inputValue ||
          (!isNaN(i?.value) && !isNaN(inputValue) && Number(i?.value) === Number(inputValue)) ||
          Number(i?.id) === Number(inputValue)
      );
    if (selectedOption) {
      onChange(selectedOption?.value || selectedOption?.id);
    }
  };

  const hiddenOptions = useMemo(() => {
    return selectedItems?.length > maxToShow ? selectedItems.slice(0, maxToShow) : [];
  }, [selectedItems, maxToShow]);

  const options = useMemo(() => {
    // Filter current options based on input value
    let filteredOptions = currOptions;

    if (inputValue && !loadOptions) {
      filteredOptions = currOptions.filter(option => option.label.toLowerCase().includes(inputValue.toLowerCase()));
    }

    const baseOptions = isMulti ? filteredOptions.filter(x => !hiddenOptions.includes(x.value)) : filteredOptions;

    if (includeSelectAll) {
      return [selectAllOption, ...baseOptions];
    }
    return baseOptions;
  }, [currOptions, inputValue, loadOptions, hiddenOptions, includeSelectAll, isMulti]);

  const handleChange = useCallback(
    selectedOptions => {
      if (isMulti && selectedOptions.some(option => option.value === 'all')) {
        onChange(items);
      } else {
        onChange(selectedOptions);
      }
    },
    [onChange, isMulti, items]
  );

  const value = useMemo(() => {
    if (!selectedItems) return [];
    if (!isMulti && isGroupped) {
      return items.flatMap(item => item?.options).filter(item => item?.value === selectedItems);
    }
    if (includeSelectAll && selectedItems.length === items.length) {
      return [selectAllOption];
    }
    return (isMulti ? selectedItems : [selectedItems]).map(item => ({
      label: item?.label || item?.name,
      value: item?.value || item._id,
      ...item
    }));
  }, [items, selectedItems, isMulti, isGroupped, includeSelectAll]);

  const MoreSelectedBadge = ({ items }) => {
    const title = items.join(', ');
    const length = items.length;
    const label = maxItemCustomMessage(length);

    return (
      <div className="smallFont px-2 mb-1" title={title}>
        {label}
      </div>
    );
  };

  const MultiValue = ({ index, getValue, ...props }) => {
    const overflow = getValue()
      .slice(maxToShow)
      .map(x => x.label);

    return index < maxToShow ? (
      <components.MultiValue {...props} />
    ) : index === maxToShow ? (
      <MoreSelectedBadge items={showMessageOnlyOnOverflow ? getValue() : overflow} />
    ) : null;
  };

  const commonProps = {
    isLoading,
    isDisabled: disabled,
    components: {
      MultiValue: CustomMultiValueComponent || MultiValue,
      ...(CustomSingleValueComponent && {
        SingleValue: CustomSingleValueComponent
      }),
      ...(CustomOptionComponent && { Option: CustomOptionComponent })
    },
    styles: styles({ fieldColors, height, isGroupped, isInvalid }),
    isMulti,
    defaultOptions: options,
    value,
    onInputChange: setInputValue,
    onBlur: handleBlur,
    isClearable,
    placeholder,
    className: height ? 'custom-multi-select custom-multi-select--compact' : 'custom-multi-select',
    classNamePrefix: 'select',
    ...(useMenuPortal ? { menuPortalTarget: document.body } : {}),
    onChange: handleChange,
    hideSelectedOptions: false,
    closeMenuOnSelect: isMulti ? closeMenuOnSelect : true,
    closeMenuOnScroll,
    menuPlacement: 'auto',
    ...(useMenuPortal ? { menuPosition: 'fixed' } : {}),
    noOptionsMessage: () => 'No items, type to search...',
    onMenuClose: () => {
      setInputValue('');
      setStopFetching(true);
      searchable && setCurrOptions(originalOptions);
    },
    filterOption: (option, inputValue) => {
      // Async options are already filtered server-side / in loadOptions
      if (loadOptions) return true;
      if (!inputValue) return true;
      return (
        option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
        option.value.toLowerCase().includes(inputValue.toLowerCase())
      );
    }
  };

  if (!creatable && !searchable) {
    return <Select {...commonProps} options={options} />;
  }

  // If creatable is true, use AsyncCreatableSelect with create option only
  if (creatable) {
    return (
      <ACS
        {...commonProps}
        onCreateOption={newValue => onCreateOption?.(newValue)}
        noOptionsMessage={() => noOptionsMessage || 'No items, type to create...'}
        formatCreateLabel={inputValue => `${creatableLabel} "${inputValue}"`}
        defaultOptions={loadOptions ? true : options}
        loadOptions={loadOptions}
      />
    );
  }

  return (
    <AS
      {...commonProps}
      {...(onCreateOption && {
        onCreateOption: inputValue => {
          onCreateOption(inputValue);
        }
      })}
      defaultOptions={loadOptions ? true : options}
      isSearchable
      noOptionsMessage={() => 'No options available'}
      loadOptions={loadOptions ? loadOptions : undefined}
    />
  );
};

export default AsyncCreatableSelect;
